import { COLLABORATION_PROTOCOL_VERSION, contentHash, decodeMessage, encodeMessage, randomId, type CollaborationMessage, type CollaborationParticipant, type PlaybackSyncState } from "./collaboration-protocol";
import { WebRtcPeer, type PeerStatus } from "./webrtc-peer";
import type { ScriptDocument, ScriptEntry } from "./types";

export type CollaborationMode = "inactive" | "host" | "client";

export interface CollaborationViewState {
  mode: CollaborationMode;
  status: PeerStatus | "idle";
  message: string;
  offer: string;
  answer: string;
  connectedPeers: number;
  participants: CollaborationParticipant[];
  localName: string;
  pendingPeerId?: string;
}

export interface CollaborativeSessionEvents {
  changed(): void;
  receivedScript(script: ScriptDocument, state?: PlaybackSyncState): void;
  receivedState(state: PlaybackSyncState): void;
  roleUnlinked(roleId: string): void;
}

interface HostPeer { peer: WebRtcPeer; status: PeerStatus; participantId?: string }

export class CollaborativeSession {
  readonly supported = typeof RTCPeerConnection !== "undefined";
  private mode: CollaborationMode = "inactive";
  private sessionId = "";
  private participantId = "";
  private localName = "";
  private localRoleIds: string[] = [];
  private participants: CollaborationParticipant[] = [];
  private hostPeers = new Map<string, HostPeer>();
  private clientPeer?: WebRtcPeer;
  private pendingPeerId?: string;
  private offer = "";
  private answer = "";
  private status: PeerStatus | "idle" = "idle";
  private message = "";
  private script?: ScriptDocument;
  private playlist: string[] = [];
  private sequence = 0;
  private lastReceivedSequence = -1;
  private queuedState?: PlaybackSyncState;
  private heartbeat?: number;

  constructor(private readonly events: CollaborativeSessionEvents, private readonly stateProvider: () => Omit<PlaybackSyncState, "sequence" | "sentAt">) {}

  viewState(): CollaborationViewState {
    return {
      mode: this.mode, status: this.status, message: this.message, offer: this.offer, answer: this.answer,
      connectedPeers: [...this.hostPeers.values()].filter((item) => item.status === "connected").length,
      participants: structuredClone(this.participants), localName: this.localName, pendingPeerId: this.pendingPeerId,
    };
  }

  isClient(): boolean { return this.mode === "client"; }
  isHost(): boolean { return this.mode === "host"; }

  startHost(script: ScriptDocument, name: string, roleIds: string[]): void {
    if (!this.supported) throw new Error("WebRTC is not available in this browser.");
    this.requireName(name);
    this.end();
    this.mode = "host";
    this.sessionId = randomId();
    this.participantId = randomId();
    this.localName = name.trim();
    this.localRoleIds = this.validRoles(roleIds, script);
    this.participants = [{ id: this.participantId, name: this.localName, roleIds: this.localRoleIds, isHost: true }];
    this.script = collaborationCopy(script);
    this.playlist = dialogueLines(this.script).map((line) => line.id);
    this.sequence = 0;
    this.status = "idle";
    this.message = "Hosting. Add a participant to create their invitation.";
    this.startHeartbeat();
    this.events.changed();
  }

  async createInvite(stunServers: string[]): Promise<void> {
    if (this.mode !== "host") throw new Error("Start hosting first.");
    if (this.hostPeers.size >= 8) throw new Error("This session has reached its eight-participant limit.");
    if (this.pendingPeerId) this.removePeer(this.pendingPeerId);
    const peerId = randomId();
    this.pendingPeerId = peerId;
    this.offer = "";
    this.message = "Gathering direct connection details…";
    const item: HostPeer = { status: "new", peer: undefined as unknown as WebRtcPeer };
    item.peer = new WebRtcPeer(this.hostEvents(peerId, item), stunServers);
    this.hostPeers.set(peerId, item);
    this.events.changed();
    try {
      this.offer = await item.peer.createOffer(this.sessionId, peerId);
      this.message = "Share this unique invitation with one participant, then import their answer.";
    } catch (error) { this.removePeer(peerId); this.fail(error); }
    this.events.changed();
  }

  async acceptAnswer(encoded: string): Promise<void> {
    const peerId = this.pendingPeerId;
    const item = peerId ? this.hostPeers.get(peerId) : undefined;
    if (!peerId || !item) throw new Error("Create a participant invitation first.");
    this.message = "Applying participant answer…";
    this.events.changed();
    try {
      await item.peer.acceptAnswer(encoded, this.sessionId, peerId);
      this.offer = "";
      this.pendingPeerId = undefined;
      this.message = "Answer accepted. Waiting for the direct connection…";
    } catch (error) { this.fail(error); }
    this.events.changed();
  }

  async join(encodedOffer: string, stunServers: string[], name: string): Promise<void> {
    if (!this.supported) throw new Error("WebRTC is not available in this browser.");
    this.requireName(name);
    this.end();
    this.mode = "client";
    this.participantId = randomId();
    this.localName = name.trim();
    this.localRoleIds = [];
    this.status = "gathering";
    this.message = "Creating your response…";
    this.clientPeer = new WebRtcPeer(this.clientEvents(), stunServers);
    this.events.changed();
    try {
      const result = await this.clientPeer.answerOffer(encodedOffer);
      this.sessionId = result.sessionId;
      this.answer = result.answer;
      this.message = "Share this answer with the host. Keep this screen open while they import it.";
    } catch (error) { this.clientPeer?.close(); this.clientPeer = undefined; this.fail(error); }
    this.events.changed();
  }

  updateProfile(roleIds: string[]): void {
    this.localRoleIds = this.validRoles(roleIds, this.script);
    if (this.mode === "host") {
      this.participants = this.participants.map((participant) => participant.id === this.participantId ? { ...participant, roleIds: this.localRoleIds } : participant);
      this.broadcastRoster();
      this.events.changed();
    } else if (this.mode === "client") {
      this.clientPeer?.send(encodeMessage({ type: "profile", protocolVersion: 1, sessionId: this.sessionId, participantId: this.participantId, name: this.localName, roleIds: this.localRoleIds }));
    }
  }

  unlinkRole(participantId: string, roleId: string): void {
    if (this.mode !== "host" || participantId === this.participantId) return;
    const participant = this.participants.find((item) => item.id === participantId);
    if (!participant?.roleIds.includes(roleId)) return;
    participant.roleIds = participant.roleIds.filter((id) => id !== roleId);
    const peer = [...this.hostPeers.values()].find((item) => item.participantId === participantId);
    peer?.peer.send(encodeMessage({ type: "unlink-role", protocolVersion: 1, sessionId: this.sessionId, participantId, roleId }));
    this.broadcastRoster();
    this.events.changed();
  }

  broadcastState(): void {
    if (this.mode !== "host") return;
    this.sequence += 1;
    this.broadcast({ type: "playback-state", protocolVersion: COLLABORATION_PROTOCOL_VERSION, sessionId: this.sessionId, ...this.stateProvider(), sequence: this.sequence, sentAt: Date.now() });
  }

  end(): void {
    if (this.mode === "host" && this.sessionId) this.broadcast({ type: "participant-left", protocolVersion: 1, sessionId: this.sessionId, participantId: this.participantId });
    this.hostPeers.forEach((item) => item.peer.close());
    this.hostPeers.clear();
    this.clientPeer?.close();
    this.clientPeer = undefined;
    if (this.heartbeat !== undefined) window.clearInterval(this.heartbeat);
    this.heartbeat = undefined;
    this.mode = "inactive";
    this.sessionId = "";
    this.offer = "";
    this.answer = "";
    this.pendingPeerId = undefined;
    this.status = "idle";
    this.message = "";
    this.participants = [];
    this.localName = "";
    this.localRoleIds = [];
    this.queuedState = undefined;
    this.lastReceivedSequence = -1;
  }

  private hostEvents(peerId: string, item: HostPeer) {
    return {
      status: (status: PeerStatus) => { item.status = status; this.status = status; if (status === "failed") this.message = "A direct connection could not be established. This network may require a TURN relay."; this.events.changed(); },
      open: () => {
        this.message = "Participant connected. Waiting for their profile…";
        try {
          if (this.script) {
            item.peer.send(encodeMessage({ type: "script-snapshot", protocolVersion: 1, sessionId: this.sessionId, script: this.script, playlist: this.playlist, contentHash: contentHash(this.script) }));
            item.peer.send(encodeMessage({ type: "playback-state", protocolVersion: 1, sessionId: this.sessionId, ...this.stateProvider(), sequence: this.sequence, sentAt: Date.now() }));
            item.peer.send(this.rosterMessage());
          }
        } catch (error) { this.fail(error); }
        this.events.changed();
      },
      message: (value: string) => this.handleHostMessage(peerId, item, value),
      close: () => {
        if (item.status !== "closed") item.status = "disconnected";
        if (item.participantId) this.participants = this.participants.filter((participant) => participant.id !== item.participantId);
        this.broadcastRoster();
        this.events.changed();
      },
    };
  }

  private clientEvents() {
    return {
      status: (status: PeerStatus) => { this.status = status; if (status === "failed") this.message = "A direct connection could not be established. This network may require a TURN relay."; this.events.changed(); },
      open: () => {
        this.answer = "";
        this.message = "Connected. Waiting for the host's script…";
        this.clientPeer?.send(encodeMessage({ type: "hello", protocolVersion: 1, sessionId: this.sessionId, participantId: this.participantId, name: this.localName, roleIds: this.localRoleIds }));
        this.events.changed();
      },
      message: (value: string) => this.handleClientMessage(value),
      close: () => { if (this.mode === "client") { this.message = "Connection closed. Create a new pairing code to reconnect."; this.events.changed(); } },
    };
  }

  private handleHostMessage(peerId: string, peer: HostPeer, value: string): void {
    try {
      const message = decodeMessage(value, this.sessionId);
      if (message.type === "ping") peer.peer.send(encodeMessage({ type: "pong", protocolVersion: 1, sessionId: this.sessionId, sentAt: message.sentAt }));
      else if (message.type === "hello") {
        if (peer.participantId && peer.participantId !== message.participantId) throw new Error("A peer attempted to change participant identity.");
        if (this.participants.some((participant) => participant.id === message.participantId)) throw new Error("A duplicate participant identity was rejected.");
        peer.participantId = message.participantId;
        this.participants.push({ id: message.participantId, name: message.name.trim(), roleIds: this.validRoles(message.roleIds, this.script), isHost: false });
        this.message = `${message.name.trim()} connected.`;
        this.broadcastRoster();
        this.events.changed();
      } else if (message.type === "profile") {
        if (!peer.participantId || peer.participantId !== message.participantId) throw new Error("A profile update has an invalid participant identity.");
        this.participants = this.participants.map((participant) => participant.id === message.participantId ? { ...participant, name: message.name.trim(), roleIds: this.validRoles(message.roleIds, this.script) } : participant);
        this.broadcastRoster();
        this.events.changed();
      }
    } catch (error) { this.fail(error); }
  }

  private handleClientMessage(value: string): void {
    try {
      const message = decodeMessage(value, this.sessionId);
      if (message.type === "script-snapshot") {
        if (contentHash(message.script) !== message.contentHash) throw new Error("The received script did not pass its integrity check.");
        const lines = dialogueLines(message.script as ScriptDocument);
        if (!lines.length || new Set(lines.map((line) => line.id)).size !== lines.length || lines.some((line, index) => line.id !== message.playlist[index]) || lines.length !== message.playlist.length) throw new Error("The host's shared line order is invalid.");
        this.script = collaborationCopy(message.script as ScriptDocument);
        this.playlist = message.playlist;
        this.message = "Connected and following the host.";
        this.events.receivedScript(this.script, this.queuedState);
        this.queuedState = undefined;
        this.events.changed();
      } else if (message.type === "playback-state") {
        const state = playbackStateFrom(message);
        if (state.sequence <= this.lastReceivedSequence) return;
        this.lastReceivedSequence = state.sequence;
        if (!this.script) this.queuedState = state;
        else if (!this.playlist.includes(state.lineId)) this.clientPeer?.send(encodeMessage({ type: "error", protocolVersion: 1, sessionId: this.sessionId, message: "Unknown host line ID." }));
        else this.events.receivedState(state);
      } else if (message.type === "roster") {
        this.participants = message.participants;
        this.events.changed();
      } else if (message.type === "unlink-role" && message.participantId === this.participantId) {
        this.localRoleIds = this.localRoleIds.filter((id) => id !== message.roleId);
        this.events.roleUnlinked(message.roleId);
      } else if (message.type === "ping") this.clientPeer?.send(encodeMessage({ type: "pong", protocolVersion: 1, sessionId: this.sessionId, sentAt: message.sentAt }));
      else if (message.type === "participant-left") { this.message = "The host ended the rehearsal."; this.status = "closed"; this.events.changed(); }
      else if (message.type === "error") { this.message = message.message; this.events.changed(); }
    } catch (error) { this.fail(error); }
  }

  private broadcast(message: CollaborationMessage): void {
    const encoded = encodeMessage(message);
    this.hostPeers.forEach((item) => item.peer.send(encoded));
  }
  private rosterMessage(): string { return encodeMessage({ type: "roster", protocolVersion: 1, sessionId: this.sessionId, participants: this.participants }); }
  private broadcastRoster(): void { if (this.mode === "host") this.hostPeers.forEach((item) => item.peer.send(this.rosterMessage())); }

  private startHeartbeat(): void {
    this.heartbeat = window.setInterval(() => {
      if (this.mode !== "host") return;
      this.broadcast({ type: "playback-state", protocolVersion: 1, sessionId: this.sessionId, ...this.stateProvider(), sequence: this.sequence, sentAt: Date.now() });
    }, 5_000);
  }

  private removePeer(peerId: string): void {
    const item = this.hostPeers.get(peerId);
    item?.peer.close();
    this.hostPeers.delete(peerId);
    if (item?.participantId) this.participants = this.participants.filter((participant) => participant.id !== item.participantId);
    if (this.pendingPeerId === peerId) this.pendingPeerId = undefined;
    this.broadcastRoster();
  }
  private validRoles(roleIds: string[], script: ScriptDocument | undefined): string[] {
    const valid = new Set(script?.characters.map((character) => character.id) ?? []);
    return [...new Set(roleIds)].filter((id) => valid.has(id));
  }
  private requireName(name: string): void { if (!name.trim()) throw new Error("Enter your name before starting or joining a rehearsal."); }
  private fail(error: unknown): void { this.status = "failed"; this.message = error instanceof Error ? error.message : "The collaboration connection failed."; }
}

function collaborationCopy(script: ScriptDocument): ScriptDocument {
  const copy = structuredClone(script);
  copy.settings.selectedCharacterIds = [];
  copy.memorisation = {};
  return copy;
}
function dialogueLines(script: ScriptDocument): ScriptEntry[] {
  if (!Array.isArray(script.pages)) throw new Error("The shared script has no valid pages.");
  const entries: ScriptEntry[] = [];
  for (const page of script.pages) {
    if (!page || !Array.isArray(page.entries)) throw new Error("The shared script contains an invalid page.");
    for (const entry of page.entries) {
      if (!entry || typeof entry.id !== "string" || typeof entry.text !== "string" || typeof entry.kind !== "string") throw new Error("The shared script contains an invalid entry.");
      if (entry.kind === "dialogue") entries.push(entry);
    }
  }
  return entries;
}
function playbackStateFrom(message: Extract<CollaborationMessage, { type: "playback-state" }>): PlaybackSyncState {
  return { lineId: message.lineId, fallbackIndex: message.fallbackIndex, revealedWords: message.revealedWords, lineShown: message.lineShown, speaking: message.speaking, sequence: message.sequence, sentAt: message.sentAt };
}
